import { of } from 'rxjs'

import { ajax, deleteRest, get, post, postWithoutBody, put } from 'utils/rxfetch'
import { buildURL } from 'utils/urls'

import { isSimulationReportRedesignPath } from './simulationCampaignInfoFixture'
import { simulationDepartmentOutcomesFixture } from './simulationDepartmentOutcomesFixture'
import { simulationFunnelFixture } from './simulationFunnelFixture'
import { simulationOverviewFixture } from './simulationOverviewFixture'

export default () => ({
  details(companyId = '', partner, campaignFlowType = '') {
    return get(
      buildURL('/members-api/awareness/details', {
        companyFilter: companyId,
        partner,
        campaignFlowType,
      })
    )
  },
  // Campaigns
  getSettings(companyId = '', trainingIds) {
    return get(
      buildURL(`/members-api/campaign/settings?companyFilter=${companyId}`, { trainingIds })
    )
  },
  saveNotifyManagerDefaults(companyId = '', body) {
    return post(`/members-api/campaign/notify-manager-defaults?companyFilter=${companyId}`, body)
  },
  getCampaigns({
    page,
    sortProperty,
    campaignTypeFilter,
    languagesFilter,
    statusFilter,
    launchTypeFilter,
    dateFilter,
    search,
    companyId = '',
    partner,
  }) {
    const params = {
      page,
      partner,
      feDateRange: dateFilter,
      sortProperty,
      statusFilter,
      languagesFilter,
      flowType: campaignTypeFilter,
      companyFilter: companyId,
      launchTypeFilter,
      search,
    }
    return post(`/members-api/campaigns?companyFilter=${companyId}`, params)
  },
  getLanguages(companyId = '') {
    return get(`/members-api/campaign/languages?companyFilter=${companyId}`)
  },
  copyCampaign(id, companiesIds) {
    return postWithoutBody(`/members-api/campaign/${id}/copy`, {
      companiesIds: companiesIds.join(','),
    })
  },
  cancelCampaign(id) {
    return post(`/members-api/campaign/${id}/cancel`)
  },
  endCampaign(id) {
    return post(`/members-api/campaign/${id}/end`)
  },
  deleteCampaign(id) {
    return deleteRest(`/members-api/campaign/${id}`)
  },
  getRecommendedCampaigns(companyId = '', partner) {
    return get(`/members-api/campaign-templates/?companyFilter=${companyId}&partner=${partner}`)
  },
  resendTraining(campaignId, body = {}) {
    const { companyFilter, ...payload } = body
    return put(buildURL(`/members-api/campaign/${campaignId}/resend`, { companyFilter }), payload)
  },
  // Scenarios
  getScenario(companyId = '', partner, id) {
    return get(`/members-api/templates/${id}/?companyFilter=${companyId}&partner=${partner}`)
  },
  getScenarios(
    page,
    sortProperty,
    categoryFilter,
    languagesFilter,
    typeFilter,
    levelFilter,
    createdByFilter,
    search,
    companyId = '',
    partner
  ) {
    return get(
      buildURL('/members-api/templates/', {
        page,
        sortProperty,
        typeFilter,
        levelFilter,
        createdByFilter,
        companyFilter: companyId,
        languagesFilter,
        categoryFilter,
        search,
        partner,
      })
    )
  },
  addScenario(body, companyId = '', partner) {
    return post(`/members-api/templates/?companyFilter=${companyId}&partner=${partner}`, body)
  },
  deleteScenario(id, companyId = '', partner) {
    return deleteRest(`/members-api/templates/${id}/?companyFilter=${companyId}&partner=${partner}`)
  },
  prepareScenarioCopy(id, name, companyId) {
    return get(
      `/members-api/scenarios/${id}/copy?companyFilter=${companyId}&name=${encodeURIComponent(
        name || ''
      )}`
    )
  },
  copyScenario(id, companiesIds, name, companyId = '') {
    return put(`/members-api/scenarios/${id}/copy?companyFilter=${companyId}`, {
      companiesIds,
      name,
    })
  },
  previewScenario(id, locale, companyId = '', partner) {
    return get(
      `/members-api/scenarios/${id}/${locale}/preview?companyFilter=${companyId}&partner=${partner}`
    )
  },
  saveScenario(id, body, companyId = '', partner) {
    return put(`/members-api/templates/${id}/?companyFilter=${companyId}&partner=${partner}`, body)
  },
  testScenario(companyId = '', id, locale, testGroup, deliveryType) {
    const data = {
      testGroup,
      deliveryType,
    }
    return post(`/members-api/scenarios/${id}/${locale}/test?companyFilter=${companyId}`, data)
  },
  // Landing Pages
  fetchLandingPages(page, search, companyId = '', partner) {
    return get(
      buildURL('/members-api/landing-pages/', {
        companyFilter: companyId,
        partner,
        page,
        search,
      })
    )
  },
  getLandingPage(id, companyId = '', partner) {
    return get(`/members-api/landing-pages/${id}/?companyFilter=${companyId}&partner=${partner}`)
  },
  previewLandingPage(id, locale) {
    return get(`/members-api/landing-pages/${id}/${locale}/preview`)
  },
  addLandingPage(body, companyId = '', partner) {
    return post(`/members-api/landing-pages/?companyFilter=${companyId}&partner=${partner}`, body)
  },
  deleteLandingPage(id, companyId = '', partner) {
    return deleteRest(
      `/members-api/landing-pages/${id}/?companyFilter=${companyId}&partner=${partner}`
    )
  },
  copyLandingPage(id, companiesIds) {
    return put(`/members-api/landing-pages/${id}/copy`, { companiesIds })
  },
  editLandingPage(id, body, companyId, partner) {
    return put(
      `/members-api/landing-pages/${id}/?companyFilter=${companyId}&partner=${partner}`,
      body
    )
  },
  // Call For Action Pages
  getCallForActionPages(page, search, companyId = '', partner) {
    return get(
      buildURL('/members-api/call-for-action-pages/', {
        page,
        companyFilter: companyId,
        partner,
        search,
      })
    )
  },
  addCallForAction(body, companyId = '', partner) {
    return post(
      `/members-api/call-for-action-pages/?companyFilter=${companyId}&partner=${partner}`,
      body
    )
  },
  getCallForAction(id, companyId = '', partner) {
    return get(
      `/members-api/call-for-action-pages/${id}/?companyFilter=${companyId}&partner=${partner}`
    )
  },
  deleteCallForAction(id, companyId = '', partner) {
    return deleteRest(
      `/members-api/call-for-action-pages/${id}/?companyFilter=${companyId}&partner=${partner}`
    )
  },
  copyCallForAction(id, companyId = '', partner) {
    return put(
      `/members-api/callforaction/${id}/copy?companyFilter=${companyId}&partner=${partner}`
    )
  },
  editCallForAction(id, body, companyId = '', partner) {
    return put(
      `/members-api/call-for-action-pages/${id}/?companyFilter=${companyId}&partner=${partner}`,
      body
    )
  },
  createNewCampaignFromScenario(id, localeId, companyId = '', partner) {
    return post(
      `/members-api/scenarios/${id}/create_campaign?companyFilter=${companyId}&partner=${partner}`,
      { locale_id: localeId }
    )
  },
  reportToTemplate(reportId) {
    return get(`/members-api/templates/report-to-template/${reportId}`)
  },
  fetchAutonomousSchedule(companyId = '') {
    return get(`/members-api/autonomous-campaign-config/?companyFilter=${companyId}`)
  },
  submitAutonomousSchedule(companyId = '', months, selected, excluded) {
    return post(`/members-api/autonomous-campaign-config/?companyFilter=${companyId}`, {
      months,
      participants_choices: selected,
      participants_exclude_choices: excluded,
    })
  },
  getReportParticipantsList({
    companyFilter = '',
    partner,
    reportId,
    page,
    pageSize,
    search,
    simulationResultTypes,
    trainingResultTypes,
    departments,
    trainingScores,
    awarenessLevels,
    scenarios,
    segments,
    trainingIds,
  }) {
    return get(
      buildURL(
        `/members-api/campaign/${reportId}/participants`,
        {
          companyFilter,
          partner,
          page,
          pageSize,
          search,
          simulationResultTypes,
          trainingResultTypes,
          departments,
          trainingScores,
          awarenessLevels,
          scenarios,
          segments,
          trainingIds,
        },
        true
      )
    )
  },
  getReportSummary(companyId = '', partner, reportId, segments) {
    return get(
      buildURL(`/members-api/campaign/${reportId}/summary`, {
        companyFilter: companyId,
        partner,
        segments,
      })
    )
  },
  getCampaignHealth(companyId = '', campaignId) {
    return get(
      buildURL(`/members-api/campaign/${campaignId}/health/`, {
        companyFilter: companyId,
      })
    )
  },
  getCampaignStatusCounts(companyId = '', campaignId) {
    return get(
      buildURL(`/members-api/campaign/${campaignId}/status-counts/`, {
        companyFilter: companyId,
      })
    )
  },
  getCampaignStatusOverTime(companyId = '', campaignId) {
    return get(
      buildURL(`/members-api/campaign/${campaignId}/status-over-time/`, {
        companyFilter: companyId,
      })
    )
  },
  getCampaignCompletionProgress(companyId = '', campaignId) {
    return get(
      buildURL(`/members-api/campaign/${campaignId}/completion-progress/`, {
        companyFilter: companyId,
      })
    )
  },
  getCampaignInfo(companyId = '', campaignId) {
    return get(
      buildURL(`/members-api/campaign/${campaignId}/info/`, {
        companyFilter: companyId,
      })
    )
  },
  getCampaignSimulationOverview(companyId = '', campaignId) {
    // IC-52386: GET /simulation-overview/ is not ready (IC-52385).
    if (isSimulationReportRedesignPath()) {
      return of(simulationOverviewFixture(campaignId))
    }

    return get(
      buildURL(`/members-api/campaign/${campaignId}/simulation-overview/`, {
        companyFilter: companyId,
      })
    )
  },
  getCampaignSimulationFunnel(companyId = '', campaignId) {
    // IC-52388: GET /simulation-funnel/ is not ready (IC-52387).
    if (isSimulationReportRedesignPath()) {
      return of(simulationFunnelFixture(campaignId))
    }

    return get(
      buildURL(`/members-api/campaign/${campaignId}/simulation-funnel/`, {
        companyFilter: companyId,
      })
    )
  },
  getCampaignDepartmentOutcomes(companyId = '', campaignId) {
    // IC-52390: GET /department-outcomes/ is not ready (IC-52389).
    if (isSimulationReportRedesignPath()) {
      return of(simulationDepartmentOutcomesFixture(campaignId))
    }

    return get(
      buildURL(`/members-api/campaign/${campaignId}/department-outcomes/`, {
        companyFilter: companyId,
      })
    )
  },
  getCampaignFinalResults(companyId = '', campaignId) {
    return get(
      buildURL(`/members-api/campaign/${campaignId}/final-results/`, {
        companyFilter: companyId,
      })
    )
  },
  createAssignmentResends(companyId = '', campaignId, body = {}) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/assignment-resends/`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  notifyManagers(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/manual-manager-notifications`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  extendCollectionPeriod(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/extend-collection-period/`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  resetParticipantDeadlines(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/participant-deadline-resets/`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  extendParticipantDeadlines(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/participant-deadline-extensions/`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  createParticipantExclusions(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/participant-exclusions/`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  resetTrainingProgress(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/progress-resets/`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  createManualCompletions(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/manual-completions/`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  revertManualCompletions(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/manual-completion-reverts/`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  pauseParticipantReminders(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/reminder-pauses/`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  resumeParticipantReminders(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/reminder-resumes/`, {
        companyFilter: companyId,
      }),
      body
    )
  },
  getCampaignTrainingParticipants(companyId = '', campaignId, query = {}) {
    return get(
      buildURL(
        `/members-api/campaign/${campaignId}/training-participants/`,
        {
          companyFilter: companyId,
          ...query,
        },
        true
      )
    )
  },
  shareCsvReport(campaignId, body) {
    return post(`/members-api/campaign/${campaignId}/participants-report-csv/`, body)
  },
  downloadParticipantsReportCsv(companyId = '', campaignId, body) {
    return post(
      buildURL(`/members-api/campaign/${campaignId}/participants-report-csv/`, {
        companyFilter: companyId,
      }),
      body,
      undefined,
      'blob'
    )
  },
  downloadParticipantsReportCsvFile(companyId = '', campaignId, jobId) {
    return ajax({
      url: buildURL(`/members-api/campaign/${campaignId}/participants-report-csv/download/`, {
        companyFilter: companyId,
        jobId,
      }),
      method: 'GET',
      responseType: 'blob',
    })
  },
  saveManualTrainingResults(campaignId, body) {
    return post(`/members-api/campaign/${campaignId}/manual-results/`, body)
  },
  fetchSPGenerationStatus(companyId = '') {
    return get(
      buildURL(`/members-api/campaign/scenarios-generation-status/`, {
        companyFilter: companyId,
      })
    )
  },
  // group autonomous campaign
  getGroupAutonomousSchedule(startEditing = true) {
    return get(
      buildURL('/members-api/group-autonomous-campaign-config/', {
        startEditing,
      })
    )
  },
  getGroupAutonomousConfigMember(page, pageSize) {
    return get(
      buildURL('/members-api/group-autonomous-campaign-config-member/', {
        page,
        pageSize,
      })
    )
  },
  getGroupAutonomousConfigMemberCandidates() {
    return get('/members-api/group-autonomous-campaign-config-member/candidates/')
  },
  createGroupAutonomousConfigMember(body) {
    return post('/members-api/group-autonomous-campaign-config-member/', body)
  },
  deleteGroupAutonomousConfigMember(companyId) {
    return deleteRest(`/members-api/group-autonomous-campaign-config-member/${companyId}/`)
  },
  updateGroupAutonomousConfigMember(companyId, body) {
    return put(`/members-api/group-autonomous-campaign-config-member/${companyId}/`, body)
  },
  saveGroupAutonomousSchedule(body) {
    return post('/members-api/group-autonomous-campaign-config/', body)
  },
  toggleActivityStatus(id, companyId = '', body) {
    return put(
      `/members-api/campaign/${id}/toggle-activity-status?companyFilter=${companyId}`,
      body
    )
  },
})
